import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { adminApi } from "../../services/adminApi";
import { apiClient } from "../../services/apiClient";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { GapNotice } from "../../components/common/GapNotice";

const TIER_COLORS = { Free: "#635c4c", Standard: "#3b82c4", AI: "#f2a900" };

export function SubscriptionAnalyticsPage() {
  const [metrics, setMetrics] = useState(null);
  const [plans, setPlans] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    adminApi.subscriptionMetrics().then(setMetrics).catch(() => setMetrics({ by_tier: [], conversion_rate_pct: 0 }));
    apiClient.get("/user/subscription/plans").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
    adminApi.upgradeFunnel().then((r) => setFunnel(r.upgrade_funnel)).catch(() => setFunnel([]));
  }, []);

  if (!metrics || !plans) return <Spinner />;

  const priceByTier = Object.fromEntries(plans.map((p) => [p.tier, p.price_monthly_usd]));
  const totalUsers = metrics.by_tier.reduce((sum, t) => sum + t.user_count, 0) || 1;
  const freeUsers = metrics.by_tier.find((t) => t.subscription_tier === "Free")?.user_count || 0;
  const paidUsers = totalUsers - freeUsers;

  const revenueByTier = metrics.by_tier
    .filter((t) => t.subscription_tier !== "Free")
    .map((t) => ({ tier: t.subscription_tier, revenue: t.user_count * (priceByTier[t.subscription_tier] || 0) }));
  const mrr = revenueByTier.reduce((sum, t) => sum + t.revenue, 0);
  const arpu = paidUsers > 0 ? mrr / paidUsers : 0;

  return (
    <div>
      <h1>Subscription Analytics</h1>

      <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <StatTile label="Free Users" value={freeUsers} sublabel={`${((freeUsers / totalUsers) * 100).toFixed(1)}% of total`} />
        <StatTile label="Paid Users" value={paidUsers} sublabel={`${((paidUsers / totalUsers) * 100).toFixed(1)}% of total`} />
        <StatTile label="Conversion Rate" value={`${metrics.conversion_rate_pct}%`} />
        <StatTile label="MRR" value={`$${mrr.toLocaleString()}`} sublabel="Standard + AI tier revenue" />
        <StatTile label="ARPU" value={`$${arpu.toFixed(2)}`} sublabel="Avg revenue per paid user" />
      </div>

      <Card title="Free vs. Paid" className="section-spacing">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={metrics.by_tier} dataKey="user_count" nameKey="subscription_tier" outerRadius={110} label>
              {metrics.by_tier.map((t) => (
                <Cell key={t.subscription_tier} fill={TIER_COLORS[t.subscription_tier] || "#888"} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Revenue by Tier" className="section-spacing">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revenueByTier}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tier" />
            <YAxis />
            <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
            <Bar dataKey="revenue" fill="#f2a900" name="Monthly Revenue" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Tier Transitions (Upgrade/Downgrade Funnel)" className="section-spacing">
        {funnel?.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={(r) => `${r.old_tier} → ${r.new_tier}`} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="transition_count" fill="#3b82c4" name="Users" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: "var(--color-text-muted)" }}>No tier transitions recorded yet.</p>
        )}
      </Card>

      <Card title="Churn Rate, MRR Growth Trend & Lifetime Value">
        <GapNotice>
          MRR above is a real point-in-time calculation (current tier counts × api/services/subscription.py's plan
          prices), but a growth *trend* needs monthly MRR snapshots that aren't stored anywhere yet. Monthly churn rate
          needs a way to distinguish "downgraded to Free" from "account closed" in app.UserSubscriptionLogs (today it
          only records tier transitions, not a cancellation reason). LTV projection needs both churn and average
          tenure, so it's blocked on the same gap.
        </GapNotice>
      </Card>
    </div>
  );
}
